import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Divider,
  Chip,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Person as DoctorIcon,
  CalendarMonth as DateIcon,
  Schedule as TimeIcon,
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import type { DoctorShiftFilterOptions } from '../../types/doctorShift';

interface DoctorShiftFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: DoctorShiftFilterOptions;
  onFiltersChange: (filters: DoctorShiftFilterOptions) => void;
  onApply: () => void;
  onReset: () => void;
}

/**
 * 医師シフトフィルタードロワーコンポーネント
 * 
 * 意図：
 * - 高度な検索・フィルタリング機能の提供
 * - 直感的なUI/UX
 * - フィルター条件の可視化
 * 
 * 注意点：
 * - フィルター状態の同期
 * - パフォーマンス最適化
 * 
 * 改善提案：
 * - フィルター条件の保存機能
 * - プリセットフィルター
 * - 高度な日付範囲選択
 */
export const DoctorShiftFilterDrawer: React.FC<DoctorShiftFilterDrawerProps> = ({
  open,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
}) => {
  const theme = useTheme();
  
  // ローカル状態（適用前の一時的なフィルター）
  const [localFilters, setLocalFilters] = useState<DoctorShiftFilterOptions>(filters);

  // フィルターが変更されたらローカル状態を更新
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // ローカルフィルターの更新
  const updateLocalFilter = (key: keyof DoctorShiftFilterOptions, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  // フィルター適用
  const handleApply = () => {
    onFiltersChange(localFilters);
    onApply();
    onClose();
  };

  // フィルターリセット
  const handleReset = () => {
    setLocalFilters({
      page: 1,
      page_size: filters.page_size || 10,
      sort_by: 'shift_date',
      sort_order: 'desc'
    });
    onReset();
  };

  // アクティブフィルター数の計算
  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.search) count++;
    if (localFilters.start_date) count++;
    if (localFilters.end_date) count++;
    if (localFilters.time_range_start) count++;
    if (localFilters.time_range_end) count++;
    if (localFilters.doctor_ids?.length) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90vw',
            borderRadius: '8px 0 0 8px',
            boxShadow: `0 0 20px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* ヘッダー */}
          <Box
            sx={{
              p: 3,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FilterIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  フィルター
                </Typography>
                {activeFilterCount > 0 && (
                  <Chip
                    label={activeFilterCount}
                    size="small"
                    color="primary"
                    sx={{ minWidth: 24, height: 24 }}
                  />
                )}
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* フィルター内容 */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Stack spacing={3}>
              {/* 医師ID検索 */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DoctorIcon fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      医師ID検索
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    placeholder="医師IDを入力..."
                    value={localFilters.search || ''}
                    onChange={(e) => updateLocalFilter('search', e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                      sx: { borderRadius: 2 }
                    }}
                  />
                </AccordionDetails>
              </Accordion>

              {/* 日付範囲フィルター */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DateIcon fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      シフト日付
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <DatePicker
                      label="開始日"
                      value={localFilters.start_date ? new Date(localFilters.start_date) : null}
                      onChange={(date) => updateLocalFilter('start_date', date ? format(date, 'yyyy-MM-dd') : undefined)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            sx: { borderRadius: 2 }
                          }
                        }
                      }}
                    />
                    <DatePicker
                      label="終了日"
                      value={localFilters.end_date ? new Date(localFilters.end_date) : null}
                      onChange={(date) => updateLocalFilter('end_date', date ? format(date, 'yyyy-MM-dd') : undefined)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            sx: { borderRadius: 2 }
                          }
                        }
                      }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* 時間帯フィルター */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      勤務時間帯
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <TimePicker
                      label="開始時間（以降）"
                      ampm={false}
                      value={localFilters.time_range_start ? new Date(`2000-01-01T${localFilters.time_range_start}`) : null}
                      onChange={(time) => updateLocalFilter('time_range_start', time ? format(time, 'HH:mm') : undefined)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            sx: { borderRadius: 2 }
                          }
                        }
                      }}
                    />
                    <TimePicker
                      label="終了時間（以前）"
                      ampm={false}
                      value={localFilters.time_range_end ? new Date(`2000-01-01T${localFilters.time_range_end}`) : null}
                      onChange={(time) => updateLocalFilter('time_range_end', time ? format(time, 'HH:mm') : undefined)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            sx: { borderRadius: 2 }
                          }
                        }
                      }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* アクティブフィルター表示 */}
              <AnimatePresence>
                {activeFilterCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      適用中のフィルター
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {localFilters.search && (
                        <Chip
                          label={`医師ID: ${localFilters.search}`}
                          onDelete={() => updateLocalFilter('search', undefined)}
                          size="small"
                        />
                      )}
                      {localFilters.start_date && (
                        <Chip
                          label={`開始日: ${localFilters.start_date}`}
                          onDelete={() => updateLocalFilter('start_date', undefined)}
                          size="small"
                        />
                      )}
                      {localFilters.end_date && (
                        <Chip
                          label={`終了日: ${localFilters.end_date}`}
                          onDelete={() => updateLocalFilter('end_date', undefined)}
                          size="small"
                        />
                      )}
                      {localFilters.time_range_start && (
                        <Chip
                          label={`開始時間: ${localFilters.time_range_start}以降`}
                          onDelete={() => updateLocalFilter('time_range_start', undefined)}
                          size="small"
                        />
                      )}
                      {localFilters.time_range_end && (
                        <Chip
                          label={`終了時間: ${localFilters.time_range_end}以前`}
                          onDelete={() => updateLocalFilter('time_range_end', undefined)}
                          size="small"
                        />
                      )}
                    </Stack>
                  </motion.div>
                )}
              </AnimatePresence>
            </Stack>
          </Box>

          {/* フッターアクション */}
          <Box
            sx={{
              p: 3,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
            }}
          >
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<ClearIcon />}
                sx={{ flex: 1, borderRadius: 2 }}
              >
                リセット
              </Button>
              <Button
                variant="contained"
                onClick={handleApply}
                startIcon={<FilterIcon />}
                sx={{ flex: 2, borderRadius: 2 }}
              >
                フィルター適用
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </LocalizationProvider>
  );
};